import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getProject } from '../api/projects'
import { getElectricalPanelById } from '../api/electricalPanels'
import { getSpecificationItem } from '../api/specification'
import { getDeviceContacts } from '../api/deviceContacts'
import CableInstances from './CableInstances'

export default function CableViewPage() {
  const { projectId, panelId, cableId } = useParams<{ projectId: string; panelId?: string; cableId: string }>()

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(Number(projectId)),
    enabled: !!projectId,
  })

  const { data: panel } = useQuery({
    queryKey: ['electricalPanel', panelId],
    queryFn: () => getElectricalPanelById(Number(panelId!)),
    enabled: !!panelId,
  })

  const { data: cable, isLoading: cableLoading, error: cableError } = useQuery({
    queryKey: ['cable', cableId],
    queryFn: () => getSpecificationItem(Number(cableId!)),
    enabled: !!cableId,
  })

  const { data: contacts = [], isLoading: contactsLoading } = useQuery({
    queryKey: ['cableContacts', cableId],
    queryFn: () => getDeviceContacts(Number(cableId!)),
    enabled: !!cableId,
  })

  if (projectLoading || cableLoading) {
    return <div className="text-center py-8">Загрузка...</div>
  }

  if (!project) {
    return <div className="text-center py-8 text-red-600">Проект не найден</div>
  }

  if (cableError || !cable) {
    return (
      <div className="text-center py-8 text-red-600">
        Кабель не найден
        <div className="mt-4">
          <Link 
            to={panelId ? `/projects/${projectId}/panels/${panelId}/cables` : `/projects/${projectId}/cables`}
            className="text-blue-600 hover:text-blue-800"
          >
            ← Вернуться к списку кабелей
          </Link>
        </div>
      </div>
    )
  }

  // Группируем контакты по жилам
  const wires = contacts.reduce((acc: any[], contact) => {
    const wireNumber = contact.contact_number.split('-')[0]
    const existingWire = acc.find(w => w.number === wireNumber)
    
    if (existingWire) {
      if (contact.contact_number.includes('TOP')) {
        existingWire.topContact = contact
      } else if (contact.contact_number.includes('BOTTOM')) {
        existingWire.bottomContact = contact
      }
    } else {
      const newWire: any = { number: wireNumber }
      if (contact.contact_number.includes('TOP')) {
        newWire.topContact = contact
      } else if (contact.contact_number.includes('BOTTOM')) {
        newWire.bottomContact = contact
      }
      acc.push(newWire)
    }
    
    return acc
  }, []).sort((a, b) => parseInt(a.number) - parseInt(b.number))

  // Цветовые коды
  const CONTACT_COLORS: Record<string, { label: string; bg: string; text: string }> = {
    WH: { label: 'Белый', bg: 'bg-white', text: 'text-gray-900' },
    BU: { label: 'Синий', bg: 'bg-blue-500', text: 'text-white' },
    BN: { label: 'Коричневый', bg: 'bg-amber-800', text: 'text-white' },
    GY: { label: 'Серый', bg: 'bg-gray-500', text: 'text-white' },
    GN: { label: 'Зеленый', bg: 'bg-green-500', text: 'text-white' },
    YE: { label: 'Желтый', bg: 'bg-yellow-400', text: 'text-gray-900' },
    RD: { label: 'Красный', bg: 'bg-red-500', text: 'text-white' },
    OG: { label: 'Оранжевый', bg: 'bg-orange-500', text: 'text-white' },
    PK: { label: 'Розовый', bg: 'bg-pink-400', text: 'text-gray-900' },
    VT: { label: 'Фиолетовый', bg: 'bg-purple-500', text: 'text-white' },
    GNYE: { label: 'Желто-зеленый', bg: 'bg-green-400', text: 'text-gray-900' },
    BK: { label: 'Черный', bg: 'bg-gray-900', text: 'text-white' },
  }

  const getColorInfo = (colorCode: string) => {
    return CONTACT_COLORS[colorCode] || { label: colorCode, bg: 'bg-gray-300', text: 'text-gray-900' }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <Link to={`/projects/${projectId}`} className="text-blue-600 hover:text-blue-800 mb-2 inline-block">
            ← Назад к {project.name}
          </Link>
          <Link 
            to={panelId ? `/projects/${projectId}/panels/${panelId}/cables` : `/projects/${projectId}/cables`}
            className="text-blue-600 hover:text-blue-800 mb-2 ml-4 inline-block"
          >
            ← К списку кабелей
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            📡 {cable.name}
            {panel && (
              <span className="text-lg font-normal text-gray-600 ml-2">
                → {panel.proj_id} ({panel.name})
              </span>
            )}
          </h1>
          <p className="text-gray-600 mt-1">
            Просмотр информации о кабеле и его экземплярах
          </p>
        </div>
      </div>

      {/* Cable Information */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Информация о кабеле</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-gray-500">ID</div>
            <div className="text-lg font-medium text-gray-900">#{cable.id}</div>
          </div>
          {cable.manufacturer && (
            <div>
              <div className="text-sm text-gray-500">Производитель</div>
              <div className="text-lg font-medium text-gray-900">{cable.manufacturer}</div>
            </div>
          )}
          {cable.model && (
            <div>
              <div className="text-sm text-gray-500">Модель</div>
              <div className="text-lg font-medium text-gray-900">{cable.model}</div>
            </div>
          )}
          {cable.length_meters && (
            <div>
              <div className="text-sm text-gray-500">Длина (м)</div>
              <div className="text-lg font-medium text-gray-900">{cable.length_meters}</div>
            </div>
          )}
          {cable.properties?.wire_count && (
            <div>
              <div className="text-sm text-gray-500">Количество жил</div>
              <div className="text-lg font-medium text-gray-900">{cable.properties.wire_count}</div>
            </div>
          )}
          {cable.properties?.section && (
            <div>
              <div className="text-sm text-gray-500">Сечение (мм²)</div>
              <div className="text-lg font-medium text-gray-900">{cable.properties.section}</div>
            </div>
          )}
          {cable.properties?.cable_type && (
            <div>
              <div className="text-sm text-gray-500">Тип кабеля</div>
              <div className="text-lg font-medium text-gray-900">{cable.properties.cable_type}</div>
            </div>
          )}
          {cable.properties?.voltage_rating && (
            <div>
              <div className="text-sm text-gray-500">Номинальное напряжение</div>
              <div className="text-lg font-medium text-gray-900">{cable.properties.voltage_rating}</div>
            </div>
          )}
          {cable.properties?.fire_safety && (
            <div>
              <div className="text-sm text-gray-500">Пожарная безопасность</div>
              <div className="text-lg font-medium text-gray-900">{cable.properties.fire_safety}</div>
            </div>
          )}
          {cable.properties?.flexibility && (
            <div>
              <div className="text-sm text-gray-500">Гибкость</div>
              <div className="text-lg font-medium text-gray-900">{cable.properties.flexibility}</div>
            </div>
          )}
          {cable.documentation_url && (
            <div>
              <div className="text-sm text-gray-500">Документация</div>
              <a 
                href={cable.documentation_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Открыть документацию
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Contacts Information */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Жилы кабеля</h2>
        {contactsLoading ? (
          <div className="text-center py-4 text-gray-500">Загрузка контактов...</div>
        ) : wires.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    № жилы
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Цвет
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Визуально
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Контакты
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {wires.map((wire) => {
                  const colorCode = wire.topContact?.color_code || wire.bottomContact?.color_code || 'WH'
                  const colorInfo = getColorInfo(colorCode)
                  return (
                    <tr key={wire.number} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {wire.number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {colorCode} - {colorInfo.label}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div 
                          className={`w-8 h-8 rounded border-2 border-gray-300 ${colorInfo.bg} ${colorInfo.text} flex items-center justify-center text-xs font-bold`}
                          title={`${colorCode} - ${colorInfo.label}`}
                        >
                          {colorCode}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {wire.topContact && wire.bottomContact ? 'TOP + BOTTOM' : wire.topContact ? 'TOP' : 'BOTTOM'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            У кабеля нет настроенных жил
          </div>
        )}
      </div>

      {/* Cable Instances */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Экземпляры кабеля</h2>
        <CableInstances 
          projectId={Number(projectId!)} 
          electricalPanelId={panelId ? Number(panelId) : undefined}
          specificationId={Number(cableId!)}
          hideCableColumn={true}
        />
      </div>
    </div>
  )
}

